import React from 'react';
import { Box, Grid, IconButton, Paper, Stack, Typography } from '@mui/material';
import VolumeOffRoundedIcon from '@mui/icons-material/VolumeOffRounded';
import VolumeUpRoundedIcon from '@mui/icons-material/VolumeUpRounded';
import Section from '../shared/Section';
import SectionHeading from '../shared/SectionHeading';
const Veggie = '/assets/images/DK7.jpg';

const workflowSteps = [
  {
    title: 'Meals, Ordered Automatically',
    description:
      'Employees set their preferences once. FoodFe takes care of the rest. Simple, safe, and convenient.',
  },
  {
    title: 'Exact Demand, Not Guesswork',
    description:
      'The kitchen gets a precise prep list. Chefs cook fresh food for the day accordingly. No stress, no leftovers.',
  },
  {
    title: 'Save Money. Eat Better.',
    description:
      'Reduced food waste, lowers costs, without compromising on healthy and tasty food choices.',
  },
];

const workflowDescription =
  'From planning to plate, our zero-waste engine guides every team on what to prep, when to deliver, and how to improve with each service.';

export const ButHow = () => {
  const [isMuted, setIsMuted] = React.useState(true);
  const videoRef = React.useRef(null);

  const handleToggleMute = () => {
    const nextMuted = !isMuted;
    setIsMuted(nextMuted);
    if (videoRef.current) {
      videoRef.current.muted = nextMuted;
    }
  };

  return (
    <Section id='ButHow' background='#FFFFFF'>
      <Grid container spacing={{ xs: 6, md: 10 }} alignItems='center'>
        <Grid item xs={12} md={6} sx={{ order: { xs: 2, md: 1 } }}>
          <Stack spacing={5}>
            <SectionHeading
              align='left'
              eyebrow='How it works'
              title="Your kitchen's new routine"
              description={workflowDescription}
            />
            <Stack spacing={3}>
              {workflowSteps.map((step, index) => (
                <Paper
                  key={step.title}
                  elevation={0}
                  sx={{
                    p: { xs: 3, md: 4 },
                    borderRadius: 24,
                    border: '1px solid',
                    borderColor: 'divider',
                    backgroundColor: 'background.paper',
                    maxWidth: { xs: '100%', md: 560 },
                    mx: { xs: 'auto', md: 0 },
                    boxShadow:
                      '0px 18px 36px rgba(31, 36, 51, 0.08), 0px 10px 24px rgba(31, 36, 51, 0.04)',
                    transition: 'transform 200ms ease, box-shadow 220ms ease, border-color 200ms ease',
                    '&:hover': {
                      transform: 'translateY(-8px)',
                      boxShadow: '0px 28px 50px rgba(31, 36, 51, 0.16)',
                      borderColor: 'primary.main',
                    },
                    '&:hover .step-index': {
                      transform: 'scale(1.08)',
                      background: 'linear-gradient(135deg, #4A5AA2 0%, #2F3A72 100%)',
                      boxShadow: '0px 12px 20px rgba(47, 58, 114, 0.25)',
                    },
                  }}
                >
                  <Stack
                    direction='row'
                    spacing={3}
                    alignItems='center'
                  >
                    <Box
                      className='step-index'
                      sx={{
                        width: 40,
                        height: 40,
                        borderRadius: '50%',
                        background: 'linear-gradient(135deg, #2F3A72 0%, #252E55 100%)',
                        flexShrink: 0,
                        display: 'grid',
                        placeItems: 'center',
                        color: '#FFFFFF',
                        fontWeight: 600,
                        fontSize: '1.6rem',
                        transition: 'transform 160ms ease, box-shadow 200ms ease, background 200ms ease',
                      }}
                    >
                      {index + 1}
                    </Box>
                    <Stack spacing={1.5}>
                      <Typography variant='h4'>{step.title}</Typography>
                      <Typography variant='body1'>{step.description}</Typography>
                    </Stack>
                  </Stack>
                </Paper>
              ))}
            </Stack>
          </Stack>
        </Grid>
        <Grid item xs={12} md={6} sx={{ order: { xs: 1, md: 2 } }}>
          <Box
            sx={{
              position: 'relative',
              width: 'min(420px, 100%)',
              mx: 'auto',
              borderRadius: 0.5,
              overflow: 'hidden',
              p: { xs: 2, md: 2.5 },
              background: 'linear-gradient(135deg, rgba(47,58,114,0.08) 0%, rgba(246,120,95,0.14) 100%)',
              boxShadow: '0px 30px 60px rgba(31, 36, 51, 0.18)',
            }}
          >
            <Box
              component='video'
              ref={videoRef}
              src='/assets/foodfe-video.mp4'
              autoPlay
              muted={isMuted}
              loop
              playsInline
              preload='metadata'
              sx={{
                width: '100%',
                height: '100%',
                objectFit: 'cover',
                display: 'block',
              }}
            />
            <IconButton
              onClick={handleToggleMute}
              aria-label={isMuted ? 'Unmute video' : 'Mute video'}
              sx={{
                position: 'absolute',
                right: 12,
                bottom: 12,
                color: '#FFFFFF',
                backgroundColor: 'rgba(0, 0, 0, 0.5)',
                '&:hover': {
                  backgroundColor: 'rgba(0, 0, 0, 0.7)',
                },
              }}
            >
              {isMuted ? <VolumeOffRoundedIcon /> : <VolumeUpRoundedIcon />}
            </IconButton>
          </Box>
        </Grid>
      </Grid>
    </Section>
  );
};
